import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type { HeartedRoom } from '@libs/trip-planner-interfaces';
import {
  AccommodationType,
  AmenitiesFilter,
  ExperienceFilter,
  AccommodationRule,
  BoardBasisFilter,
  AvailabilityOrder,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard, isDefined } from '@libs/utils';

import { ActivitySerializer } from '../../activitiySerializer';
import {
  DESTINATION_PRODUCTS_PREFIX,
  DESTINATION_PRODUCTS_SEPARATOR,
  CONSTRAINT_TEXT_SUFFIX,
} from '../constants';
import {
  serializeNumberOfNights,
  serializeTransportType,
} from '../serializeStay';

import {
  HOTEL_NAME_PREFIX,
  STAY_REFUNDABILITY_PREFIX,
  NEW_SELECTED_ROOMS_PREFIX,
  HOTEL_FILTERS_PREFIX,
  HOTEL_INGREDIENT_SUFFIX,
  STAY_REFUNDABILITY_CODE,
  SUGGESTION_ORDER_PREFIX,
  SerializedAvailabilityOrder,
  EMPTY_DESTINATION_PRODUCTS_PREFIX,
  HOTEL_NEIGHBORHOOD_POLYGONS_PREFIX,
  ROOM_BOARDBASIS_PREFIX,
  ROOM_TYPE_ID_PREFIX,
} from './constants';

export function serializeHotel(hotel: HotelIngredient): string {
  let result = serializeConstraintText(hotel);

  result += serializeDestinationProducts(hotel);

  result += HOTEL_FILTERS_PREFIX;

  result += serializeNumberOfNights(hotel);

  result += serializeFilters(hotel);

  result += serializeTransportType(hotel);

  result += serializeAvailabilityOrder(hotel);

  result += serializeNeighborhoodFilters(hotel);

  result += HOTEL_INGREDIENT_SUFFIX;

  return result;
}

export function serializeConstraintText(hotel: HotelIngredient): string {
  let result = '';

  if (hotel.filters.filterText) {
    result += `${HOTEL_NAME_PREFIX}${hotel.filters.filterText}${CONSTRAINT_TEXT_SUFFIX}`;
  }

  result += hotel.getSearchText();

  return result;
}

export function serializeDestinationProducts(hotel: HotelIngredient): string {
  let result = '';

  if (!hotel.heartedHotels.length && !hotel.subIngredients.length) {
    return EMPTY_DESTINATION_PRODUCTS_PREFIX;
  }

  if (hotel.heartedHotels.length || hotel.subIngredients.length) {
    result += DESTINATION_PRODUCTS_PREFIX;
  }

  const heartedHotelsWithRooms = serializeHeartedHotels();

  const serializedTickets = serializeActivityTickets();

  result += [...heartedHotelsWithRooms, ...serializedTickets].join(
    DESTINATION_PRODUCTS_SEPARATOR
  );

  return result;

  function serializeHeartedHotels(): string[] {
    return hotel.heartedHotels.map((hotel) => {
      let hotelAndRoomsString = hotel.code;

      if (hotel.isRefundable) {
        hotelAndRoomsString += `${STAY_REFUNDABILITY_PREFIX}${STAY_REFUNDABILITY_CODE}`;
      }

      if (hotel.rooms && hotel.rooms.length) {
        hotelAndRoomsString += `${NEW_SELECTED_ROOMS_PREFIX}${hotel.rooms
          .map(serializeRoom)
          .join(NEW_SELECTED_ROOMS_PREFIX)}`;
      } else if (hotel.roomCodes && hotel.roomCodes.length) {
        hotelAndRoomsString += `${NEW_SELECTED_ROOMS_PREFIX}${hotel.roomCodes
          .map((code) => encodeURIComponent(code))
          .join(NEW_SELECTED_ROOMS_PREFIX)}`;
      }

      return hotelAndRoomsString;
    });

    function serializeRoom(room: HeartedRoom): string {
      let serializedRoom = `${encodeURIComponent(
        room.code
      )}${ROOM_BOARDBASIS_PREFIX}${encodeURIComponent(room.boardBasis)}`;

      if (isDefined(room.roomTypeId)) {
        serializedRoom = `${serializedRoom}${ROOM_TYPE_ID_PREFIX}${room.roomTypeId}`;
      }

      return serializedRoom;
    }
  }

  function serializeActivityTickets(): string[] {
    return hotel.subIngredients.map(ActivitySerializer.serialize);
  }
}

export function serializeFilters(hotel: HotelIngredient): string {
  let result = hotel.filters.starRating.toString();

  // Refactor: wrap '1':'0' assignations for better readability
  result += hotel.filters.carefullySelectedOnly ? '1' : '0';

  result += hotel.filters.selectedHotelTypes.includes(AccommodationType.Hotel)
    ? '1'
    : '0';

  result += hotel.filters.selectedHotelTypes.includes(
    AccommodationType.Apartment
  )
    ? '1'
    : '0';

  result += hotel.filters.selectedHotelTypes.includes(
    AccommodationType.HouseOrCottage
  )
    ? '1'
    : '0';

  result += hotel.filters.selectedAmenitiesFilters.includes(
    AmenitiesFilter.Pool
  )
    ? '1'
    : '0';

  result += hotel.filters.selectedAmenitiesFilters.includes(
    AmenitiesFilter.Parking
  )
    ? '1'
    : '0';

  result += hotel.filters.selectedExperienceFilters.includes(
    ExperienceFilter.PetsAllowed
  )
    ? '1'
    : '0';

  result += hotel.filters.selectedHotelRules.includes(
    AccommodationRule.FreeCancellation
  )
    ? '1'
    : '0';

  result += hotel.filters.boardBasis.includes(BoardBasisFilter.RoomOnly)
    ? '1'
    : '0';

  result += hotel.filters.boardBasis.includes(BoardBasisFilter.Breakfast)
    ? '1'
    : '0';

  result += hotel.filters.boardBasis.includes(BoardBasisFilter.HalfBoard)
    ? '1'
    : '0';

  result += hotel.filters.boardBasis.includes(BoardBasisFilter.FullBoard)
    ? '1'
    : '0';

  result += hotel.filters.boardBasis.includes(BoardBasisFilter.AllInclusive)
    ? '1'
    : '0';

  result += hotel.filters.selectedExperienceFilters.includes(
    ExperienceFilter.Business
  )
    ? '1'
    : '0';
  result += hotel.filters.selectedExperienceFilters.includes(
    ExperienceFilter.Family
  )
    ? '1'
    : '0';
  result += hotel.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Spa)
    ? '1'
    : '0';
  result += hotel.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Gym)
    ? '1'
    : '0';
  result += hotel.filters.selectedAmenitiesFilters.includes(
    AmenitiesFilter.Wifi
  )
    ? '1'
    : '0';
  result += hotel.filters.selectedAmenitiesFilters.includes(
    AmenitiesFilter.AirConditioned
  )
    ? '1'
    : '0';
  result += hotel.filters.selectedAmenitiesFilters.includes(
    AmenitiesFilter.VehicleChargingStation
  )
    ? '1'
    : '0';
  result += hotel.filters.selectedExperienceFilters.includes(
    ExperienceFilter.GreenSustainableProperty
  )
    ? '1'
    : '0';

  return result;
}

export function serializeNeighborhoodFilters(hotel: HotelIngredient): string {
  if (!hotel.filters.polygonIds.length) {
    return '';
  }
  let result = HOTEL_NEIGHBORHOOD_POLYGONS_PREFIX;
  result += hotel.filters.polygonIds.toString();
  return result;
}

export function serializeAvailabilityOrder(hotel: HotelIngredient): string {
  let serializedAvailabilityOrder: string;

  switch (hotel.availabilityOrder) {
    case AvailabilityOrder.None:
      serializedAvailabilityOrder = SerializedAvailabilityOrder.None;
      break;

    case AvailabilityOrder.Cheapest:
      serializedAvailabilityOrder = SerializedAvailabilityOrder.Cheapest;
      break;

    case AvailabilityOrder.Popular:
      serializedAvailabilityOrder = SerializedAvailabilityOrder.Popular;
      break;

    default:
      exhaustiveGuard(
        hotel.availabilityOrder,
        'Unhandled availability order serializer'
      );
  }

  return `${SUGGESTION_ORDER_PREFIX}${serializedAvailabilityOrder}`;
}
